import React, { useCallback, useEffect, useState } from "react";
import { Box, TextField, IconButton, useTheme } from "@mui/material";
import { GridDeleteIcon } from "@mui/x-data-grid";
import ColorPicker from "../../colorPicker";
import useStyles from "../../styles";

interface KeyColorProps {
  colorKeyIndex: number;
  type: string;
  data: [string, { text: string; color: string }];
  onUpdateKeyColor: (
    newKeyValue: [string, { text: string; color: string }],
    keyIndex: number
  ) => void;
  onRemoveKeyColor: () => void;
  openColorOptionIndex: number | null;
  setOpenColorOptionIndex: (index: number | null) => void;
  getAllColors: () => Record<string, { [key: string]: string | boolean }[]>;
  getColorFromPallet: (index: number) => string;
}

const KeyColor: React.FC<KeyColorProps> = ({
  colorKeyIndex,
  type,
  data,
  onRemoveKeyColor,
  onUpdateKeyColor,
  openColorOptionIndex,
  setOpenColorOptionIndex,
  getAllColors,
  getColorFromPallet,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [keyColorValue, setKeyColorValue] = useState<[string, any]>(["", {}]);

  useEffect(() => {
    setKeyColorValue(data);
  }, [data]);

  const handleUpdateKeyColor = useCallback(
    (newValue: [string, any]) => {
      setKeyColorValue(newValue);
      onUpdateKeyColor(newValue, colorKeyIndex);
    },
    [colorKeyIndex, onUpdateKeyColor]
  );

  return (
    <>
      {type && type === "customText" && (
        <Box
          key={colorKeyIndex}
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            sx={styles.datagridTextField}
            label="Chave"
            variant="filled"
            value={keyColorValue[0]}
            onChange={(e) =>
              handleUpdateKeyColor([e.target.value, keyColorValue[1]])
            }
          />
          <TextField
            sx={styles.datagridTextField}
            label="Nome de exibição"
            variant="filled"
            value={keyColorValue[1].text}
            onChange={(e) =>
              handleUpdateKeyColor([
                keyColorValue[0],
                { ...keyColorValue[1], text: e.target.value },
              ])
            }
          />
          <Box sx={{ display: "flex", marginBottom: 2 }}>
            <ColorPicker
              keyIndex={colorKeyIndex}
              colorKey={keyColorValue[0]}
              colorValue={keyColorValue[1].color}
              handleColorPickerSelection={(newColor: string) =>
                handleUpdateKeyColor([
                  keyColorValue[0],
                  { ...keyColorValue[1], color: newColor },
                ])
              }
              openColorOptionIndex={openColorOptionIndex}
              setOpenColorOptionIndex={setOpenColorOptionIndex}
              getAllColors={getAllColors}
              getColorFromPallet={getColorFromPallet}
            />
            <IconButton
              sx={{
                color: theme.palette.cromatic.red.main,
              }}
              onClick={onRemoveKeyColor}
            >
              <GridDeleteIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      {type && (type === "chip" || type === "progress") && (
        <Box
          key={colorKeyIndex}
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            sx={styles.datagridTextField}
            label="Chave"
            variant="filled"
            value={keyColorValue[0]}
            onChange={(e) =>
              handleUpdateKeyColor([e.target.value, keyColorValue[1]])
            }
          />
          <Box sx={{ display: "flex", marginBottom: 2 }}>
            <ColorPicker
              keyIndex={colorKeyIndex}
              colorKey={keyColorValue[0]}
              colorValue={
                typeof keyColorValue[1] === "string"
                  ? keyColorValue[1]
                  : keyColorValue[1]?.color || ""
              }
              handleColorPickerSelection={(newColor: string) =>
                handleUpdateKeyColor([
                  keyColorValue[0],
                  typeof keyColorValue[1] === "string"
                    ? newColor
                    : { ...keyColorValue[1], color: newColor },
                ])
              }
              openColorOptionIndex={openColorOptionIndex}
              setOpenColorOptionIndex={setOpenColorOptionIndex}
              getAllColors={getAllColors}
              getColorFromPallet={getColorFromPallet}
            />
            <IconButton
              sx={{
                color: theme.palette.cromatic.red.main,
              }}
              onClick={onRemoveKeyColor}
            >
              <GridDeleteIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </>
  );
};

export default KeyColor;
